import { Link } from 'braid-design-system';
import React, { type MouseEvent } from 'react';
import { useSelector } from 'react-redux';

import { getHeaderFooterInteraction } from 'src/app/analytics/headerFooter';
import { TRACKING_ORIGIN } from 'src/app/constants/headerFooterTracking';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import type AppState from 'src/types/appState';

interface TrackingLinkProps {
  href?: string;
  onClick: (event: MouseEvent, ...params: []) => void;
  ['data-analytics']: string;
  ['data-analytics-origin']: string;
  [restProps: string]: any; // allow any other props to be accepted and propagated to the rendered components
}

export const TrackingLink = (props: TrackingLinkProps) => {
  const tealium = useSelector((state: AppState) => state.tealium);

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const { href, onClick, ...restProps } = props;

  const handleClick = (event: MouseEvent, ...params: []) => {
    const trackingEventName = props['data-analytics'];
    const trackingActionOrigin = props[
      'data-analytics-origin'
    ] as TRACKING_ORIGIN;

    if (href === '#') {
      event.preventDefault();
    }

    if (trackingEventName && trackingActionOrigin) {
      const trackingEventData = getHeaderFooterInteraction(
        trackingEventName,
        trackingActionOrigin,
      );

      if (isPageAnalyticsEnabled) {
        const eventData = {
          eventName: trackingEventName,
          objectInteraction: trackingEventData.objectInteraction,
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendLinkEvent(tealium, trackingEventData);
      }
    }

    if (typeof onClick === 'function') {
      onClick(event, ...params);
      return;
    }

    return;
  };

  if (!href) {
    return null;
  }

  return <Link {...restProps} href={href} onClick={handleClick} />;
};

export const headerLinkRenderer = (rendererProps: any) => (
  <TrackingLink
    {...rendererProps}
    data-analytics-origin={TRACKING_ORIGIN.HEADER}
  />
);

export const footerLinkRenderer = (rendererProps: any) => (
  <TrackingLink
    {...rendererProps}
    data-analytics-origin={TRACKING_ORIGIN.FOOTER}
  />
);
